/**
 * Filing workflows: one workflow's folder, tags and owner, and the
 * organisation's list of folders (make, rename, delete; deleting moves its
 * workflows to "No folder", it deletes none of them).
 */
import { useEffect, useState } from 'react';
import {
  ActionIcon, Autocomplete, Badge, Button, Group, Modal, Stack, TagsInput, Text, TextInput, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconPencil, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import {
  useWorkflowEdits, useWorkflowFolderEdits, type WorkflowFolder, type WorkflowSummary,
} from '../../api/workflows';
import { useVocab } from '../../vocab';
import { OwnerSelect } from '../crm/crmShared';

export function OrganiseModal({
  workflow, folders, suggestions, tags, onClose,
}: {
  workflow: WorkflowSummary | null;
  folders: WorkflowFolder[];
  suggestions: string[];
  tags: string[];
  onClose: () => void;
}) {
  const v = useVocab();
  const edits = useWorkflowEdits();
  const [folder, setFolder] = useState('');
  const [tagList, setTagList] = useState<string[]>([]);
  const [owner, setOwner] = useState<string | null>(null);
  useEffect(() => {
    if (!workflow) return;
    setFolder(workflow.folder);
    setTagList(workflow.tags);
    setOwner(workflow.owner_user_id ?? null);
  }, [workflow]);

  async function save() {
    if (!workflow) return;
    try {
      await edits.organise.mutateAsync({
        id: workflow.id,
        body: { folder: folder.trim(), tags: tagList, owner_user_id: owner ?? '' },
      });
      notifications.show({ color: 'teal', title: 'Filed', message: folder.trim() ? `${workflow.name} is in ${folder.trim()}.` : workflow.name });
      onClose();
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not changed', message: (e as Error).message });
    }
  }

  return (
    <Modal opened={!!workflow} onClose={onClose} title={workflow ? `File ${workflow.name}` : ''} size="md">
      <Stack gap="sm">
        <Autocomplete
          label="Folder"
          description="Type a new name to make a folder. Empty is No folder."
          placeholder="No folder"
          data={[...folders.map((f) => f.name), ...suggestions]}
          value={folder}
          onChange={setFolder}
          data-testid="organise-folder"
        />
        <TagsInput
          label="Tags"
          description="Free words to find it by. Up to 10."
          placeholder="Add a tag"
          data={tags}
          value={tagList}
          onChange={(t) => setTagList(t.map((x) => x.trim().toLowerCase()).filter(Boolean))}
          maxTags={10}
          clearable
          data-testid="organise-tags"
        />
        <OwnerSelect
          label={v.One('owner')}
          description={`Who looks after this ${v.one('workflow')}.`}
          value={owner}
          onChange={setOwner}
          data-testid="organise-owner"
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={edits.organise.isPending} data-testid="organise-save">Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function FoldersModal({
  opened, onClose, folders, suggestions,
}: {
  opened: boolean;
  onClose: () => void;
  folders: WorkflowFolder[];
  suggestions: string[];
}) {
  const v = useVocab();
  const edits = useWorkflowFolderEdits();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  async function create(n: string) {
    if (!n.trim()) return;
    try {
      await edits.create.mutateAsync(n.trim());
      setName('');
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not made', message: (e as Error).message });
    }
  }
  async function rename(f: WorkflowFolder) {
    try {
      await edits.rename.mutateAsync({ id: f.id, name: draft });
      setEditing(null);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not renamed', message: (e as Error).message });
    }
  }
  async function remove(f: WorkflowFolder) {
    const moving = f.count ? ` Its ${v.count(f.count, 'workflow')} move to No folder; none is deleted.` : '';
    if (!window.confirm(`Delete the folder ${f.name}?${moving}`)) return;
    try {
      await edits.remove.mutateAsync(f.id);
      notifications.show({ color: 'teal', title: 'Folder deleted', message: f.count ? `${v.count(f.count, 'workflow')} now in No folder.` : f.name });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not deleted', message: (e as Error).message });
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Folders" size="md">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">{`One folder per ${v.one('workflow')}. Deleting a folder moves its ${v.many('workflow')} to No folder.`}</Text>
        {folders.length === 0 && <Text size="sm" c="dimmed">No folders yet.</Text>}
        {folders.map((f) => (
          <Group key={f.id} justify="space-between" wrap="nowrap" gap="xs" data-testid={`folder-${f.name}`}>
            {editing === f.id ? (
              <TextInput
                size="xs"
                value={draft}
                onChange={(e) => setDraft(e.currentTarget.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void rename(f); }}
                style={{ flex: 1 }}
                aria-label={`New name for ${f.name}`}
                autoFocus
              />
            ) : (
              <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                <Text size="sm" style={{ overflowWrap: 'anywhere' }}>{f.name}</Text>
                <Badge variant="light" color="gray" radius="sm" tt="none">{f.count}</Badge>
              </Group>
            )}
            <Group gap={4} wrap="nowrap">
              {editing === f.id ? (
                <>
                  <ActionIcon variant="subtle" aria-label="Save name" onClick={() => rename(f)}><IconCheck size={15} /></ActionIcon>
                  <ActionIcon variant="subtle" color="gray" aria-label="Cancel" onClick={() => setEditing(null)}><IconX size={15} /></ActionIcon>
                </>
              ) : (
                <>
                  <Tooltip label="Rename" openDelay={300}>
                    <ActionIcon variant="subtle" color="gray" aria-label={`Rename ${f.name}`} onClick={() => { setEditing(f.id); setDraft(f.name); }}>
                      <IconPencil size={15} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Delete" openDelay={300}>
                    <ActionIcon variant="subtle" color="red" aria-label={`Delete ${f.name}`} onClick={() => remove(f)}>
                      <IconTrash size={15} />
                    </ActionIcon>
                  </Tooltip>
                </>
              )}
            </Group>
          </Group>
        ))}
        <Group gap="xs" wrap="nowrap" align="flex-end">
          <TextInput
            label="New folder"
            placeholder="Folder name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void create(name); }}
            style={{ flex: 1 }}
            data-testid="new-folder"
          />
          <Button leftSection={<IconPlus size={14} />} onClick={() => create(name)} disabled={!name.trim()} loading={edits.create.isPending}>
            Add
          </Button>
        </Group>
        {suggestions.length > 0 && (
          <Stack gap={6}>
            <Text size="xs" c="dimmed">Suggested</Text>
            <Group gap={6}>
              {suggestions.map((s) => (
                <Button key={s} size="compact-xs" variant="light" color="gray" leftSection={<IconPlus size={12} />} onClick={() => create(s)}>
                  {s}
                </Button>
              ))}
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
